import { ColumnState } from '@ag-grid-community/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { ColorMetadataService } from '@ddv/charts';
import { ModalDialogService } from '@ddv/common-components';
import { GridState } from '@ddv/data-grid';
import {
    FieldMetadata,
    MetadataFormatUtils,
    AUTO_GROUP_COLUMN_ID,
    VizConfigs,
    GridConfigItem,
    AggType,
    ConfigureGridConfigItem,
    GridConfigs,
} from '@ddv/models';
import { uid } from '@ddv/utils';
import { Theme, ThemeService } from '@hs/ui-core-presentation';

import { DropZoneConfig } from '../../../models/drop-zone-config';
import { ColumnFormattingGridComponent } from '../column-formatting-grid/column-formatting-grid.component';
import { ConfigureComponent } from '../configure/configure.component';
import * as dropZoneConfig from '../drop-zone-config';

const CONVERSABLE_TYPE_ATTRIBUTES = 'Conversable Type Attributes';

@Component({
    selector: 'app-mw-grid-configure',
    templateUrl: './configure-grid.component.html',
    styleUrls: ['../configure/configure.component.scss'],
    standalone: false,
})
export class ConfigureGridComponent extends ConfigureComponent {
    @Input() isAttached = true;

    gridDataConfigs: ConfigureGridConfigItem[] = [];

    @ViewChild('scrollContainer') private readonly scrollContainer: ElementRef<HTMLElement> | undefined;
    private theme: Theme = Theme.light;

    constructor(
        private readonly modalService: ModalDialogService,
        private readonly themeService: ThemeService,
    ) {
        super();

        this.themeService.currentTheme$.subscribe((theme: Theme) => {
            this.theme = theme;
            this.updateColumnConditionColors();
        });
    }

    getDropZones(): DropZoneConfig[] {
        return dropZoneConfig.grid;
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    dragStarted(_: FieldMetadata): void {}

    showColumnFormatters(config: GridConfigItem): void {
        const dialogRef = this.modalService
            .open<GridConfigItem, ColumnFormattingGridComponent>(ColumnFormattingGridComponent, { windowClass: 'column-formatter-popup' });
        dialogRef.componentInstance.parentData = config;
        dialogRef.componentInstance.clientCode = this.clientCode;
        dialogRef.result.then(
            (response) => {
                if (response !== 'cancel') {
                    Object.assign(config, response);
                    this.renderPreview();
                }
            },
            (dismissData) => dismissData,
        );
    }

    onCdkDropSuccess(data: CdkDragDrop<ConfigureGridConfigItem[]>): void {
        if (data.previousContainer === data.container) {
            moveItemInArray(this.gridDataConfigs, data.previousIndex, data.currentIndex);
        } else {
            this.updateConfigList(data.item.data, data.currentIndex);
        }
        this.activelyDragging = false;
        this.renderPreview();
    }

    updateSimpleGridConfigList(fieldName: string, index: number): void {
        const metadataField = this.metadata.find((m) => m.name === fieldName);
        if (metadataField) {
            this.updateConfigList(metadataField, index);
        }
    }

    removeConfigItemAtIndex(index: number): void {
        this.gridDataConfigs.splice(index, 1);
        this.renderPreview();
    }

    override initDataConfigs(): void {
        this.gridDataConfigs = [];
    }

    setDataConfigs(configs: VizConfigs): void {
        this.gridDataConfigs = configs.values as ConfigureGridConfigItem[];
    }

    override getSelectedConfigs(): GridConfigs {
        const configs: GridConfigItem[] = this.gridDataConfigs.map((config) => {
            const { canAggregate, ...rest } = config;
            return rest;
        });

        return { values: [...configs] };
    }

    updateGridColumnsSort(columns: GridConfigItem[]): void {
        columns.forEach((column) => {
            const dataConfigById = this.dataConfigById(column.colId);
            if (dataConfigById) {
                dataConfigById.agSort = column.agSort;
                dataConfigById.agSortIndex = column.agSortIndex;
            }
        });
    }

    updateGridColumnsPinned(columns: GridConfigItem[]): void {
        columns.forEach((column) => {
            const dataConfigById = this.dataConfigById(column.colId);
            if (dataConfigById) {
                dataConfigById.pinned = column.pinned;
            }
        });
    }

    updateGridColumnsFilter(columns: GridConfigItem[]): void {
        columns.forEach((column) => {
            const dataConfigById = this.dataConfigById(column.colId);
            if (dataConfigById) {
                dataConfigById.agFilter = column.agFilter;
            }
        });
    }

    // This appears to be unsetting some configurations from configuration modals
    updateGridColumnState(gridState: GridState): void {
        if (gridState) {
            const groupColumnId = gridState.columnState.findIndex((col) => col.colId === AUTO_GROUP_COLUMN_ID);
            gridState.columnState.forEach((column) => {
                const configuredColumn = this.gridDataConfigs.find((dataConfig) => dataConfig.colId === column.colId);
                if (configuredColumn) {
                    configuredColumn.isHidden = !!column.hide;
                    configuredColumn.pivotIndex = column.pivotIndex ?? null;
                    configuredColumn.rowGroupIndex = column.rowGroupIndex ?? null;
                    configuredColumn.width = column.width ?? 0;
                    configuredColumn.pinned = column.pinned ?? null;

                    configuredColumn.customWidthWhenGroup = column.rowGroupIndex != null && groupColumnId !== -1 ?
                        gridState.columnState[groupColumnId].width ?? null :
                        null;

                    if (columnCanBeAggregated(configuredColumn, column)) {
                        configuredColumn.aggregationType = typeof column.aggFunc === 'string' ? column.aggFunc as AggType : 'none';
                    }
                }
            });
        }
    }

    override hasRequiredFields(): boolean {
        return !!this.gridDataConfigs.length;
    }

    afterMetadataUpdate(): void {
        if (this.isCrosstalkGrid()) {
            const udfColumnNames = this.getUdfColumnNames();
            this.removeDeletedUdfColumnsFromGridConfigs(udfColumnNames);
        }
    }

    protected onDoubleClick(metadata: FieldMetadata): void {
        this.gridDataConfigs.push(getConfigItem(metadata));
        if (this.scrollContainer) {
            this.scrollContainer.nativeElement.scrollTop = this.scrollContainer.nativeElement.scrollHeight;
        }
        this.renderPreview();
    }

    private updateConfigList(metadata: FieldMetadata, index: number): void {
        this.gridDataConfigs = [
            ...this.gridDataConfigs.slice(0, index),
            getConfigItem(metadata),
            ...this.gridDataConfigs.slice(index),
        ];
    }

    private dataConfigById(colId: string): ConfigureGridConfigItem | undefined {
        return this.gridDataConfigs.find((column) => column.colId === colId);
    }

    private getUdfColumnNames(): string[] {
        return this.metadata.reduce((udfColumnNamesList: string[], metadataObj) => {
            if (this.isUserDefinedFieldColumn(metadataObj.name)) {
                udfColumnNamesList.push(metadataObj.name);
            }
            return udfColumnNamesList;
        }, []);
    }

    private removeDeletedUdfColumnsFromGridConfigs(udfColumnNames: string[]): void {
        for (let i = this.gridDataConfigs.length - 1; i >= 0; i--) {
            const config = this.gridDataConfigs[i];
            if (this.isUserDefinedFieldColumn(config.value) && !udfColumnNames.includes(config.value)) {
                this.gridDataConfigs.splice(i, 1);
            }
        }
    }

    private isCrosstalkGrid(): boolean | undefined {
        return this.groupedMetadata?.some((metadataGroup) => metadataGroup.displayName === CONVERSABLE_TYPE_ATTRIBUTES);
    }

    private isUserDefinedFieldColumn(field: string | undefined): boolean | undefined {
        return field?.startsWith('udf_');
    }

    private updateColumnConditionColors(): void {
        if (this.gridDataConfigs?.length) {
            this.gridDataConfigs.forEach((config) =>
                config.columnCondition?.forEach((c) => ColorMetadataService.mapThemeColors(this.theme, c)));
        }
    }
}

function getConfigItem(metadata: FieldMetadata): ConfigureGridConfigItem {
    return {
        ...MetadataFormatUtils.getDefaultColFormatters(),
        colId: uid(),
        label: metadata.displayName ?? '',
        value: metadata.name,
        datatype: metadata.datatype ?? '',
        displayType: metadata.displayType ?? 'string',
        alignment: metadata.alignment ?? 'left',
        aggregationType: metadata.displayType === 'value' ? 'sum' : undefined,
        canAggregate: !!metadata.canAggregate,
    };
}

function columnCanBeAggregated(
    configuredColumn: ConfigureGridConfigItem,
    column: ColumnState,
): boolean {
    return (configuredColumn.canAggregate || configuredColumn.datatype === 'number' || configuredColumn.datatype === 'decimal')
        && Object.prototype.hasOwnProperty.call(column, 'aggFunc');
}
